import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faSpinner, faArrowLeft, faBoxArchive, faUserGroup,
  faCalendar, faLink, faTag, faComment, faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { apiJson, apiForm, apiDownload, baixarBlob, getCookie } from '../apiClient';
import { PageHeader } from '../../crm/components/ui/PageHeader';
import { Panel } from '../../crm/components/ui/Panel';
import { KpiCard, KpiGrid } from '../../crm/components/ui/KpiCard';
import { Badge, StatusBadge } from '../../crm/components/ui/Badge';
import { Button } from '../../crm/components/ui/Button';
import { Tabs } from '../../crm/components/ui/Tabs';
import { Sheet } from '../../crm/components/ui/Sheet';
import { EmptyState } from '../../crm/components/ui/EmptyState';
import { SegmentedControl } from '../../crm/components/ui/SegmentedControl';
import { AssinaturaSheet } from '../../crm/components/financeiro/AssinaturaSheet';
import { LancamentoSheet } from '../../crm/components/financeiro/LancamentoSheet';
import { BulkActionBar } from '../../crm/components/ui/BulkActionBar';

const apiProj = (path, opts) => apiJson(path, opts);
const apiFin = (path, opts) => apiJson(`/financeiro${path}`, opts);
const apiClientesFn = (path, opts) => apiJson(`/clientes${path}`, opts);
const apiEst = (path, opts) => apiJson(`/estoque${path}`, opts);
const apiPat = (path, opts) => apiJson(`/patrimonio${path}`, opts);

// Usuários do tenant vêm do backend legado (Cofre) — plataforma de
// Auth/Usuário é compartilhada entre Cofre e CRM. `buscarTodos` é acessível
// pra qualquer usuário autenticado (sem exigir cargo Admin/root), ao
// contrário de `usuarios/buscar` — por isso usamos esse endpoint aqui em
// vez do usado em Empresas.jsx (tela restrita a Super Admin).
async function buscarUsuariosTenant() {
  try {
    const token = getCookie('info');
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/buscarTodos`, { headers: { Authority: token } });
    const result = await res.json();
    return result.status === 'ok' ? result.lista : [];
  } catch {
    return [];
  }
}

const fmtBRL = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');

const COLUNAS_KANBAN = [
  { key: 'A_FAZER', label: 'A Fazer' },
  { key: 'EM_ANDAMENTO', label: 'Em Andamento' },
  { key: 'EM_REVISAO', label: 'Em Revisão' },
  { key: 'CONCLUIDA', label: 'Concluída' },
];
const PRIORIDADE_TONE = { BAIXA: 'neutral', MEDIA: 'warning', ALTA: 'danger' };
const PRIORIDADE_LABEL = { BAIXA: 'Baixa', MEDIA: 'Média', ALTA: 'Alta' };

// Item 8 do diagnóstico de usabilidade: checklist padrão aplicada sozinha
// na criação de um projeto — reaproveita `POST /tarefas` em loop, sem
// endpoint/tabela nova. O modelo não vira um "tipo de projeto" persistido
// (esse conceito não existe hoje no domínio) — é só um atalho de criação.
const MODELOS_CHECKLIST = {
  '': [],
  'Onboarding de cliente': ['Reunião de kickoff', 'Coletar acessos e credenciais', 'Definir escopo e prazos', 'Enviar proposta/contrato assinado'],
  'Desenvolvimento de site/sistema': ['Levantamento de requisitos', 'Wireframe/protótipo', 'Desenvolvimento', 'Testes', 'Publicação', 'Treinamento do cliente'],
  'Consultoria': ['Diagnóstico inicial', 'Apresentação de plano de ação', 'Execução', 'Relatório final'],
};

// ─── Lista de projetos ───────────────────────────────────────────────────
function ListaProjetos({ onAbrirProjeto, showToast, clienteIdParaNovoProjeto, onProjetoCriado }) {
  const [projetos, setProjetos] = useState(null);
  const [busca, setBusca] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ nome: '', descricao: '' });
  const [modeloChecklist, setModeloChecklist] = useState('');
  const [salvando, setSalvando] = useState(false);

  const fetchProjetos = useCallback(async () => {
    const result = await apiProj('/projetos');
    if (result.status === 'ok') setProjetos(result.lista);
    else showToast?.(result.mensagem || 'Erro ao carregar projetos', 'error');
  }, [showToast]);

  useEffect(() => { fetchProjetos(); }, [fetchProjetos]);
  useEffect(() => { if (clienteIdParaNovoProjeto) setShowModal(true); }, [clienteIdParaNovoProjeto]);

  const criarProjeto = async () => {
    if (!form.nome.trim()) { showToast?.('Informe o nome do projeto', 'error'); return; }
    setSalvando(true);
    const result = await apiProj('/projetos', { method: 'POST', body: JSON.stringify(form) });
    if (result.status !== 'ok') { setSalvando(false); showToast?.(result.mensagem || 'Erro ao criar projeto', 'error'); return; }

    const itensChecklist = MODELOS_CHECKLIST[modeloChecklist] || [];
    if (itensChecklist.length > 0) {
      await Promise.all(itensChecklist.map((titulo) => apiProj(`/projetos/${result.projeto.id}/tarefas`, { method: 'POST', body: JSON.stringify({ titulo, status: 'A_FAZER' }) })));
    }

    setSalvando(false);
    setShowModal(false);
    setForm({ nome: '', descricao: '' });
    setModeloChecklist('');
    if (clienteIdParaNovoProjeto) {
      await apiProj(`/projetos/${result.projeto.id}/clientes/${clienteIdParaNovoProjeto}`, { method: 'POST' });
      showToast?.('Projeto criado e cliente associado!', 'success');
      onProjetoCriado?.(result.projeto.id);
    } else {
      showToast?.(itensChecklist.length > 0 ? `Projeto criado com ${itensChecklist.length} tarefa(s) da checklist!` : 'Projeto criado!', 'success');
      fetchProjetos();
    }
  };

  const filtrados = (projetos || []).filter((p) => {
    if (statusFiltro && p.status !== statusFiltro) return false;
    if (busca && !p.nome.toLowerCase().includes(busca.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <PageHeader
        icon={faBoxArchive}
        title="Projetos"
        subtitle="Entregas, tarefas e status de cada projeto em andamento."
        actions={<Button variant="primary" icon={faPlus} onClick={() => setShowModal(true)}>Projeto</Button>}
      />

      <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap', marginBottom: 'var(--crm-space-4)' }}>
        <div className="crm-search-input" style={{ minWidth: '220px' }}>
          <FontAwesomeIcon icon={faSearch} />
          <input className="crm-input" placeholder="Buscar projeto..." value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <select className="crm-select" value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)}>
          <option value="">Status: Todos</option>
          <option value="PLANEJAMENTO">Planejamento</option>
          <option value="EM_ANDAMENTO">Em andamento</option>
          <option value="PAUSADO">Pausado</option>
          <option value="CONCLUIDO">Concluído</option>
          <option value="CANCELADO">Cancelado</option>
        </select>
      </div>

      <Panel flush>
        {projetos === null ? (
          <EmptyState title="Carregando..." />
        ) : filtrados.length === 0 ? (
          <EmptyState
            title={projetos.length === 0 ? 'Nenhum projeto ainda' : 'Nenhum projeto encontrado'}
            description={projetos.length === 0 ? 'Crie o primeiro projeto para organizar tarefas, financeiro e equipamentos.' : undefined}
            actions={projetos.length === 0 ? <Button variant="primary" icon={faPlus} onClick={() => setShowModal(true)}>Projeto</Button> : undefined}
          />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Projeto</th><th>Clientes</th><th>Status</th><th>Tarefas</th></tr></thead>
              <tbody>
                {filtrados.map((p) => (
                  <tr key={p.id} className="clickable" onClick={() => onAbrirProjeto(p.id)}>
                    <td>
                      <div className="crm-table-primary">{p.nome}</div>
                      {p.descricao && <div className="crm-table-sub">{p.descricao}</div>}
                    </td>
                    <td>{p.clientes.length === 0 ? '—' : p.clientes.map((c) => c.nome).join(', ')}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td>{p.tarefas_total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Sheet open={showModal} onClose={() => setShowModal(false)} title="Novo Projeto" footer={<Button variant="primary" loading={salvando} onClick={criarProjeto}>Criar Projeto</Button>}>
        <div className="crm-form-field">
          <label className="crm-form-label">Nome</label>
          <input className="crm-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome do projeto" />
        </div>
        <div className="crm-form-field">
          <label className="crm-form-label">Descrição (opcional)</label>
          <textarea className="crm-input" rows={3} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
        </div>
        <div className="crm-form-field">
          <label className="crm-form-label">Modelo de checklist (opcional)</label>
          <select className="crm-select" value={modeloChecklist} onChange={(e) => setModeloChecklist(e.target.value)}>
            <option value="">Nenhum — começar em branco</option>
            {Object.keys(MODELOS_CHECKLIST).filter((k) => k).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          {modeloChecklist && <div className="crm-form-hint" style={{ marginTop: 'var(--crm-space-1)' }}>Cria {MODELOS_CHECKLIST[modeloChecklist].length} tarefa(s) padrão automaticamente em "A Fazer".</div>}
        </div>
      </Sheet>
    </div>
  );
}

function categoriaAtividade(action) {
  if (action.startsWith('TAREFA_')) return 'accent';
  if (action.startsWith('RECEIVABLE') || action.startsWith('PAYABLE') || action.startsWith('PAYMENT') || action.startsWith('BILLING') || action.startsWith('RECURRENCE')) return 'positive';
  if (action.startsWith('CLIENTE_')) return 'warning';
  if (action.startsWith('PROJETO_')) return 'accent';
  return 'neutral';
}

// ─── Aba Visão Geral ─────────────────────────────────────────────────────
function AbaVisaoGeral({ cockpit }) {
  if (!cockpit) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;
  const { tarefas, financeiro, atividade_recente } = cockpit;
  const cards = [
    { label: 'Tarefas abertas', valor: tarefas.abertas },
    { label: 'Tarefas atrasadas', valor: tarefas.atrasadas, tone: tarefas.atrasadas > 0 ? 'danger' : undefined },
    { label: 'Tarefas concluídas', valor: tarefas.concluidas },
    { label: 'Receita', valor: fmtBRL(financeiro.receita) },
    { label: 'Despesa', valor: fmtBRL(financeiro.despesa) },
    { label: 'Resultado', valor: fmtBRL(financeiro.resultado), tone: parseFloat(financeiro.resultado) < 0 ? 'danger' : undefined },
    { label: 'A receber', valor: fmtBRL(financeiro.a_receber) },
    { label: 'Vencido', valor: fmtBRL(financeiro.vencido), tone: parseFloat(financeiro.vencido) > 0 ? 'danger' : undefined },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
      <KpiGrid>{cards.map((c) => <KpiCard key={c.label} label={c.label} value={c.valor} tone={c.tone} />)}</KpiGrid>
      <Panel title="Atividade recente" flush>
        {atividade_recente.length === 0 ? (
          <EmptyState title="Nenhuma atividade ainda" />
        ) : (
          <div className="crm-timeline">
            {atividade_recente.map((a, i) => (
              <div className="crm-timeline-item" key={i}>
                <Badge tone={categoriaAtividade(a.tipo)}>{a.tipo.split('_')[0].toLowerCase()}</Badge>
                <span className="crm-timeline-desc">{a.descricao}</span>
                <span className="crm-timeline-when">{fmtDateTime(a.quando)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ─── Aba Histórico ───────────────────────────────────────────────────────
function AbaHistorico({ projetoId, clienteFiltro }) {
  const [logs, setLogs] = useState(null);
  const [limite, setLimite] = useState(50);

  const carregar = useCallback(async () => {
    const qs = new URLSearchParams({ project_id: projetoId, limite: String(limite) });
    if (clienteFiltro) qs.set('client_id', clienteFiltro);
    const result = await apiProj(`/auditoria?${qs}`);
    if (result.status === 'ok') setLogs(result.lista);
  }, [projetoId, clienteFiltro, limite]);

  useEffect(() => { carregar(); }, [carregar]);

  if (logs === null) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;
  if (logs.length === 0) return <EmptyState title="Nenhuma atividade registrada ainda" />;

  return (
    <Panel flush>
      <div className="crm-timeline">
        {logs.map((log) => (
          <div className="crm-timeline-item" key={log.id}>
            <Badge tone={categoriaAtividade(log.action)}>{log.action.split('_')[0].toLowerCase()}</Badge>
            <span className="crm-timeline-desc">{log.description}</span>
            <span className="crm-timeline-when">{fmtDateTime(log.created_em)}</span>
          </div>
        ))}
      </div>
      {logs.length >= limite && (
        <div style={{ padding: 'var(--crm-space-4)' }}><Button variant="outline" onClick={() => setLimite(limite + 50)}>Carregar mais</Button></div>
      )}
    </Panel>
  );
}

// ─── Sheet de Tarefa (completa: cliente/responsável/prioridade/prazo/tags/
// links/notas/status — §10.5 do redesign) ────────────────────────────────
function TarefaSheet({ tarefa, projeto, clienteFiltro, usuarios, onFechar, onSalvo, onArquivar, showToast }) {
  const [form, setForm] = useState({
    titulo: tarefa.titulo, descricao: tarefa.descricao || '', prioridade: tarefa.prioridade,
    data_vencimento: tarefa.data_vencimento || '', cliente_id: tarefa.cliente_id || '',
    responsavel_id: tarefa.responsavel_id || '',
  });
  const [tags, setTags] = useState(tarefa.tags || []);
  const [novaTag, setNovaTag] = useState('');
  const [links, setLinks] = useState(tarefa.links || []);
  const [novoLink, setNovoLink] = useState({ titulo: '', url: '' });
  const [notas, setNotas] = useState(tarefa.notas || []);
  const [novaNota, setNovaNota] = useState('');

  const salvar = () => onSalvo(tarefa.id, {
    ...form, data_vencimento: form.data_vencimento || null, cliente_id: form.cliente_id || null,
    responsavel_id: form.responsavel_id || null, tags, links,
  });

  const adicionarTag = () => { if (novaTag.trim()) { setTags([...tags, novaTag.trim()]); setNovaTag(''); } };
  const removerTag = (i) => setTags(tags.filter((_, idx) => idx !== i));
  const adicionarLink = () => { if (novoLink.url.trim()) { setLinks([...links, novoLink]); setNovoLink({ titulo: '', url: '' }); } };
  const removerLink = (i) => setLinks(links.filter((_, idx) => idx !== i));

  const enviarNota = async () => {
    if (!novaNota.trim()) return;
    const r = await apiProj(`/projetos/${projeto.id}/tarefas/${tarefa.id}/notas`, { method: 'POST', body: JSON.stringify({ texto: novaNota }) });
    if (r.status === 'ok') { setNotas(r.tarefa.notas); setNovaNota(''); }
    else showToast?.(r.mensagem || 'Erro ao adicionar nota', 'error');
  };

  return (
    <Sheet
      open onClose={onFechar} title="Tarefa"
      footer={<>
        <Button variant="ghost" icon={faBoxArchive} onClick={() => onArquivar(tarefa.id)}>Arquivar</Button>
        <Button variant="primary" onClick={salvar}>Salvar</Button>
      </>}
    >
      <div className="crm-form-field">
        <label className="crm-form-label">Título</label>
        <input className="crm-input" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
      </div>
      <div className="crm-form-field">
        <label className="crm-form-label">Descrição</label>
        <textarea className="crm-input" rows={3} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
      </div>
      <div className="crm-form-field">
        <label className="crm-form-label">Prioridade</label>
        <select className="crm-select" value={form.prioridade} onChange={(e) => setForm({ ...form, prioridade: e.target.value })}>
          <option value="BAIXA">Baixa</option><option value="MEDIA">Média</option><option value="ALTA">Alta</option>
        </select>
      </div>
      <div className="crm-form-field">
        <label className="crm-form-label">Prazo</label>
        <input className="crm-input" type="date" value={form.data_vencimento} onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })} />
      </div>
      <div className="crm-form-field">
        <label className="crm-form-label">Cliente</label>
        <select className="crm-select" value={form.cliente_id} onChange={(e) => setForm({ ...form, cliente_id: e.target.value })} disabled={!!clienteFiltro}>
          <option value="">Geral (sem cliente)</option>
          {projeto.clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
      </div>
      <div className="crm-form-field">
        <label className="crm-form-label">Responsável</label>
        <select className="crm-select" value={form.responsavel_id} onChange={(e) => setForm({ ...form, responsavel_id: e.target.value })}>
          <option value="">Sem responsável</option>
          {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>
      </div>

      <div className="crm-form-field">
        <label className="crm-form-label"><FontAwesomeIcon icon={faTag} /> Tags</label>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap', marginBottom: 'var(--crm-space-2)' }}>
          {tags.map((t, i) => (
            <Badge key={i} tone="accent">{t} <span style={{ cursor: 'pointer', marginLeft: 4 }} onClick={() => removerTag(i)}>×</span></Badge>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <input className="crm-input" placeholder="Nova tag" value={novaTag} onChange={(e) => setNovaTag(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && adicionarTag()} />
          <Button size="sm" variant="outline" onClick={adicionarTag}>+</Button>
        </div>
      </div>

      <div className="crm-form-field">
        <label className="crm-form-label"><FontAwesomeIcon icon={faLink} /> Links</label>
        {links.map((l, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)', marginBottom: 4 }}>
            <a href={l.url} target="_blank" rel="noreferrer" style={{ color: 'var(--crm-accent-hover)' }}>{l.titulo || l.url}</a>
            <span style={{ cursor: 'pointer', color: 'var(--crm-text-tertiary)' }} onClick={() => removerLink(i)}>×</span>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <input className="crm-input" placeholder="Título" value={novoLink.titulo} onChange={(e) => setNovoLink({ ...novoLink, titulo: e.target.value })} style={{ maxWidth: '120px' }} />
          <input className="crm-input" placeholder="URL" value={novoLink.url} onChange={(e) => setNovoLink({ ...novoLink, url: e.target.value })} />
          <Button size="sm" variant="outline" onClick={adicionarLink}>+</Button>
        </div>
      </div>

      <div className="crm-form-field">
        <label className="crm-form-label"><FontAwesomeIcon icon={faComment} /> Notas</label>
        {notas.map((n, i) => (
          <div key={i} style={{ fontSize: 'var(--crm-text-sm)', padding: 'var(--crm-space-2) 0', borderBottom: '1px solid var(--crm-border)' }}>
            <div>{n.texto}</div>
            <div className="crm-form-hint">{n.autor_nome || 'Usuário'} — {fmtDateTime(n.criado_em)}</div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', marginTop: 'var(--crm-space-2)' }}>
          <input className="crm-input" placeholder="Adicionar nota..." value={novaNota} onChange={(e) => setNovaNota(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && enviarNota()} />
          <Button size="sm" variant="outline" onClick={enviarNota}>Enviar</Button>
        </div>
      </div>
    </Sheet>
  );
}

// ─── Aba Operação (Kanban com drag-and-drop) ────────────────────────────
function AbaOperacao({ projetoId, projeto, clienteFiltro, showToast, onMudou }) {
  const [tarefas, setTarefas] = useState(null);
  const [novaTarefaColuna, setNovaTarefaColuna] = useState(null);
  const [tituloNovaTarefa, setTituloNovaTarefa] = useState('');
  const [clienteNovaTarefa, setClienteNovaTarefa] = useState('');
  const [tarefaAberta, setTarefaAberta] = useState(null);
  const [arrastando, setArrastando] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [arquivandoLote, setArquivandoLote] = useState(false);
  const [prioridadeFiltro, setPrioridadeFiltro] = useState('');
  const [apenasAtrasadas, setApenasAtrasadas] = useState(false);

  const carregar = useCallback(async () => {
    const qs = clienteFiltro ? `?cliente_id=${clienteFiltro}` : '';
    const result = await apiProj(`/projetos/${projetoId}/tarefas${qs}`);
    if (result.status === 'ok') setTarefas(result.lista);
  }, [projetoId, clienteFiltro]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { buscarUsuariosTenant().then(setUsuarios); }, []);

  const criarTarefa = async (statusColuna) => {
    if (!tituloNovaTarefa.trim()) return;
    const cliente_id = clienteFiltro || clienteNovaTarefa || undefined;
    const result = await apiProj(`/projetos/${projetoId}/tarefas`, { method: 'POST', body: JSON.stringify({ titulo: tituloNovaTarefa, status: statusColuna, cliente_id }) });
    if (result.status === 'ok') { setTituloNovaTarefa(''); setClienteNovaTarefa(''); setNovaTarefaColuna(null); carregar(); onMudou?.(); }
    else showToast?.(result.mensagem || 'Erro ao criar tarefa', 'error');
  };

  const moverTarefa = async (tarefa, novoStatus) => {
    if (tarefa.status === novoStatus) return;
    const result = await apiProj(`/projetos/${projetoId}/tarefas/${tarefa.id}`, { method: 'PUT', body: JSON.stringify({ status: novoStatus }) });
    if (result.status === 'ok') { carregar(); onMudou?.(); }
  };

  const salvarTarefa = async (tarefaId, campos) => {
    const result = await apiProj(`/projetos/${projetoId}/tarefas/${tarefaId}`, { method: 'PUT', body: JSON.stringify(campos) });
    if (result.status === 'ok') { carregar(); onMudou?.(); setTarefaAberta(null); }
    else showToast?.(result.mensagem || 'Erro ao salvar tarefa', 'error');
  };

  const arquivarTarefa = async (tarefaId) => {
    const result = await apiProj(`/projetos/${projetoId}/tarefas/${tarefaId}`, { method: 'DELETE' });
    if (result.status === 'ok') { setTarefaAberta(null); carregar(); onMudou?.(); showToast?.('Tarefa arquivada', 'success'); }
  };

  const alternarSelecaoTarefa = (id) => setSelecionadas((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const arquivarSelecionadas = async () => {
    setArquivandoLote(true);
    await Promise.all(selecionadas.map((id) => apiProj(`/projetos/${projetoId}/tarefas/${id}`, { method: 'DELETE' })));
    setArquivandoLote(false);
    showToast?.(`${selecionadas.length} tarefa(s) arquivada(s)`, 'success');
    setSelecionadas([]);
    carregar();
    onMudou?.();
  };

  if (tarefas === null) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando tarefas...</div>;

  const hojeStr = new Date().toISOString().slice(0, 10);
  const estaAtrasada = (t) => t.data_vencimento && t.data_vencimento < hojeStr && t.status !== 'CONCLUIDA';
  const tarefasVisiveis = tarefas.filter((t) => (
    (!prioridadeFiltro || t.prioridade === prioridadeFiltro)
    && (!apenasAtrasadas || estaAtrasada(t))
  ));
  const tarefasPorColuna = (col) => tarefasVisiveis.filter((t) => t.status === col).sort((a, b) => a.ordem - b.ordem);
  const nomeCliente = (id) => (projeto.clientes.find((c) => c.id === id) || {}).nome || 'Cliente';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', flexWrap: 'wrap', marginBottom: 'var(--crm-space-3)' }}>
        <select className="crm-select" value={prioridadeFiltro} onChange={(e) => setPrioridadeFiltro(e.target.value)} style={{ maxWidth: '160px' }}>
          <option value="">Todas as prioridades</option>
          <option value="ALTA">Alta</option>
          <option value="MEDIA">Média</option>
          <option value="BAIXA">Baixa</option>
        </select>
        <SegmentedControl
          size="sm"
          value={apenasAtrasadas}
          onChange={setApenasAtrasadas}
          options={[{ value: false, label: 'Todas' }, { value: true, label: 'Atrasadas' }]}
        />
      </div>
      <BulkActionBar count={selecionadas.length} processando={arquivandoLote} onArquivar={arquivarSelecionadas} onLimpar={() => setSelecionadas([])} />
      <div className="crm-kanban">
        {COLUNAS_KANBAN.map((col) => (
          <div
            className="crm-kanban-col"
            key={col.key}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (arrastando) moverTarefa(arrastando, col.key); setArrastando(null); }}
          >
            <div className="crm-kanban-col-header">
              <span>{col.label}</span>
              <span className="crm-kanban-col-count">{tarefasPorColuna(col.key).length}</span>
            </div>
            <div className="crm-kanban-col-body">
              {tarefasPorColuna(col.key).map((t) => (
                <div
                  className="crm-kanban-card"
                  key={t.id}
                  draggable
                  onDragStart={() => setArrastando(t)}
                  onDragEnd={() => setArrastando(null)}
                  onClick={() => setTarefaAberta(t)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--crm-space-2)' }}>
                    <input
                      type="checkbox"
                      checked={selecionadas.includes(t.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => alternarSelecaoTarefa(t.id)}
                      style={{ marginTop: '3px' }}
                    />
                    <div className="crm-kanban-card-title" style={{ flex: 1 }}>{t.titulo}</div>
                  </div>
                  <div className="crm-kanban-card-meta">
                    {t.cliente_id ? <Badge tone="accent">{nomeCliente(t.cliente_id)}</Badge> : <Badge tone="neutral">Geral</Badge>}
                    <Badge tone={PRIORIDADE_TONE[t.prioridade]}>{PRIORIDADE_LABEL[t.prioridade]}</Badge>
                  </div>
                  {t.data_vencimento && <div className="crm-kanban-card-meta"><FontAwesomeIcon icon={faCalendar} /> {fmtDate(t.data_vencimento)}</div>}
                  {t.responsavel_id && (
                    <div className="crm-kanban-card-meta">
                      <FontAwesomeIcon icon={faUserGroup} /> {usuarios.find((u) => u.id === t.responsavel_id)?.nome || 'Responsável'}
                    </div>
                  )}
                  {(t.tags?.length > 0) && (
                    <div className="crm-kanban-card-meta">{t.tags.map((tag, i) => <Badge key={i} tone="neutral">{tag}</Badge>)}</div>
                  )}
                </div>
              ))}

              {novaTarefaColuna === col.key ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)', padding: 'var(--crm-space-2)' }}>
                  <input autoFocus className="crm-input" placeholder="Título da tarefa" value={tituloNovaTarefa}
                    onChange={(e) => setTituloNovaTarefa(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && criarTarefa(col.key)} />
                  {!clienteFiltro && projeto.clientes.length > 0 && (
                    <select className="crm-select" value={clienteNovaTarefa} onChange={(e) => setClienteNovaTarefa(e.target.value)}>
                      <option value="">Geral (sem cliente)</option>
                      {projeto.clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  )}
                  {clienteFiltro && <div className="crm-form-hint">Cliente: {nomeCliente(clienteFiltro)} (contexto atual)</div>}
                  <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
                    <Button size="sm" variant="primary" onClick={() => criarTarefa(col.key)}>Adicionar</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setNovaTarefaColuna(null); setTituloNovaTarefa(''); }}>Cancelar</Button>
                  </div>
                </div>
              ) : (
                <button className="crm-nav-item" style={{ justifyContent: 'flex-start' }} onClick={() => setNovaTarefaColuna(col.key)}>
                  <FontAwesomeIcon icon={faPlus} /> Nova tarefa
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {tarefaAberta && (
        <TarefaSheet
          tarefa={tarefaAberta} projeto={projeto} clienteFiltro={clienteFiltro} usuarios={usuarios}
          onFechar={() => setTarefaAberta(null)} onSalvo={salvarTarefa} onArquivar={arquivarTarefa} showToast={showToast}
        />
      )}
    </div>
  );
}

// ─── Aba Documentos ──────────────────────────────────────────────────────
function AbaDocumentosProjeto({ projetoId, clienteFiltro, showToast }) {
  const [documentos, setDocumentos] = useState(null);
  const [showNovo, setShowNovo] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [arquivo, setArquivo] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    const qs = new URLSearchParams({ entity_type: 'PROJETO', entity_id: projetoId });
    const result = await apiProj(`/documentos?${qs}`);
    if (result.status === 'ok') setDocumentos(result.lista);
  }, [projetoId]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    if (!titulo.trim() || !arquivo) { showToast?.('Informe o título e selecione um arquivo', 'error'); return; }
    setSalvando(true);
    const relacoes = [{ entity_type: 'PROJETO', entity_id: projetoId }];
    if (clienteFiltro) relacoes.push({ entity_type: 'CLIENTE', entity_id: clienteFiltro });
    const formData = new FormData();
    formData.append('titulo', titulo);
    formData.append('tipo', 'OUTRO');
    formData.append('arquivo', arquivo);
    formData.append('relacoes', JSON.stringify(relacoes));
    const result = await apiForm('/documentos', { method: 'POST', body: formData });
    setSalvando(false);
    if (result.status === 'ok') { showToast?.('Documento adicionado!', 'success'); setShowNovo(false); setTitulo(''); setArquivo(null); carregar(); }
    else showToast?.(result.mensagem || 'Erro ao criar documento', 'error');
  };

  const baixar = async (documentId, versionId, nome) => {
    const blob = await apiDownload(`/documentos/${documentId}/versoes/${versionId}/download`);
    if (!blob) { showToast?.('Erro ao baixar', 'error'); return; }
    baixarBlob(blob, nome);
  };

  if (documentos === null) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /></div>;

  return (
    <Panel title="Documentos do projeto" actions={<Button size="sm" icon={faPlus} onClick={() => setShowNovo(true)}>Documento</Button>} flush>
      {documentos.length === 0 ? (
        <EmptyState title="Nenhum documento relacionado a este projeto" actions={<Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Documento</Button>} />
      ) : (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead><tr><th>Título</th><th>Tipo</th><th>Atualizado</th><th /></tr></thead>
            <tbody>
              {documentos.map((d) => (
                <tr key={d.id}>
                  <td className="crm-table-primary">{d.titulo}</td>
                  <td><Badge tone="accent">{d.tipo}</Badge></td>
                  <td>{fmtDateTime(d.atualizado_em)}</td>
                  <td>{d.versao_atual?.tem_arquivo && <Button size="sm" variant="ghost" onClick={() => baixar(d.id, d.versao_atual.id, d.titulo)}>Baixar</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Sheet open={showNovo} onClose={() => setShowNovo(false)} title="Novo Documento" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Salvar</Button>}>
        <div className="crm-form-field"><label className="crm-form-label">Título</label><input className="crm-input" value={titulo} onChange={(e) => setTitulo(e.target.value)} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Arquivo</label><input type="file" onChange={(e) => setArquivo(e.target.files[0])} /></div>
      </Sheet>
    </Panel>
  );
}

// ─── Aba Equipamentos (§10.6 — vincular/criar/gerar de compra sem sair do
// Projeto) ────────────────────────────────────────────────────────────────
function AbaEquipamentosProjeto({ projetoId, clienteFiltro, showToast, onNavegar }) {
  const [assets, setAssets] = useState(null);
  const [showVincular, setShowVincular] = useState(false);
  const [showCriar, setShowCriar] = useState(false);
  const [showGerarCompra, setShowGerarCompra] = useState(false);

  const carregar = useCallback(() => {
    const qs = new URLSearchParams({ projeto_id: projetoId });
    if (clienteFiltro) qs.set('cliente_id', clienteFiltro);
    apiPat(`/assets?${qs}`).then((r) => {
      if (r.status === 'ok') setAssets(r.lista);
      else showToast?.(r.mensagem || 'Erro ao carregar equipamentos', 'error');
    });
  }, [projetoId, clienteFiltro, showToast]);

  useEffect(() => { carregar(); }, [carregar]);

  if (assets === null) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /></div>;

  const acoes = (
    <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
      <Button size="sm" variant="outline" onClick={() => setShowVincular(true)}>Vincular existente</Button>
      <Button size="sm" variant="outline" onClick={() => setShowCriar(true)}>Criar ativo</Button>
      <Button size="sm" variant="outline" onClick={() => setShowGerarCompra(true)}>Gerar de compra</Button>
    </div>
  );

  return (
    <Panel title="Equipamentos do projeto" actions={acoes} flush>
      {assets.length === 0 ? (
        <EmptyState title="Nenhum equipamento neste projeto" description="Vincule um equipamento existente ou crie um novo, sem sair do projeto." actions={acoes} />
      ) : (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead><tr><th>Código</th><th>Equipamento</th><th>Status</th><th>Local/Cliente</th></tr></thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.id} className={onNavegar ? 'clickable' : ''} onClick={() => onNavegar?.('patrimonio', { ativoId: a.id })}>
                  <td className="crm-table-primary">{a.asset_tag}</td>
                  <td>{a.produto_nome}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td>{a.current_stock_location_nome || (a.cliente_id ? 'Com cliente' : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showVincular && (
        <VincularAtivoSheet projetoId={projetoId} clienteFiltro={clienteFiltro} onFechar={() => setShowVincular(false)} onSalvo={() => { setShowVincular(false); carregar(); }} showToast={showToast} />
      )}
      {showCriar && (
        <CriarAtivoSheet projetoId={projetoId} clienteFiltro={clienteFiltro} onFechar={() => setShowCriar(false)} onSalvo={() => { setShowCriar(false); carregar(); }} showToast={showToast} />
      )}
      {showGerarCompra && (
        <GerarDeCompraSheet projetoId={projetoId} clienteFiltro={clienteFiltro} onFechar={() => setShowGerarCompra(false)} onSalvo={() => { setShowGerarCompra(false); carregar(); }} showToast={showToast} />
      )}
    </Panel>
  );
}

function VincularAtivoSheet({ projetoId, clienteFiltro, onFechar, onSalvo, showToast }) {
  const [disponiveis, setDisponiveis] = useState(null);
  const [selecionado, setSelecionado] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { apiPat('/assets?status=DISPONIVEL').then((r) => { if (r.status === 'ok') setDisponiveis(r.lista); }); }, []);

  const salvar = async () => {
    if (!selecionado) { showToast?.('Selecione um equipamento', 'error'); return; }
    setSalvando(true);
    const r = await apiPat(`/assets/${selecionado}/entregar`, { method: 'POST', body: JSON.stringify({ projeto_id: projetoId, cliente_id: clienteFiltro || undefined }) });
    setSalvando(false);
    if (r.status === 'ok') { showToast?.('Equipamento vinculado!', 'success'); onSalvo(); }
    else showToast?.(r.mensagem || 'Erro ao vincular', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Vincular equipamento existente" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Vincular</Button>}>
      {disponiveis === null ? (
        <div className="crm-form-hint">Carregando...</div>
      ) : disponiveis.length === 0 ? (
        <div className="crm-form-hint">Nenhum equipamento disponível em estoque.</div>
      ) : (
        <div className="crm-form-field">
          <label className="crm-form-label">Equipamento</label>
          <select className="crm-select" value={selecionado} onChange={(e) => setSelecionado(e.target.value)}>
            <option value="">Selecione...</option>
            {disponiveis.map((a) => <option key={a.id} value={a.id}>{a.asset_tag} — {a.produto_nome}</option>)}
          </select>
        </div>
      )}
    </Sheet>
  );
}

function CriarAtivoSheet({ projetoId, clienteFiltro, onFechar, onSalvo, showToast }) {
  const [produtos, setProdutos] = useState([]);
  const [produtoId, setProdutoId] = useState('');
  const [showNovoProduto, setShowNovoProduto] = useState(false);
  const [nomeNovoProduto, setNomeNovoProduto] = useState('');
  const [assetTag, setAssetTag] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carregarProdutos = useCallback(() => {
    apiEst('/produtos').then((r) => { if (r.status === 'ok') setProdutos(r.lista); });
  }, []);
  useEffect(() => { carregarProdutos(); }, [carregarProdutos]);

  const criarProdutoInline = async () => {
    if (!nomeNovoProduto.trim()) return;
    const r = await apiEst('/produtos', { method: 'POST', body: JSON.stringify({ nome: nomeNovoProduto, tipo_controle: 'RASTREAVEL' }) });
    if (r.status === 'ok') {
      showToast?.('Produto criado!', 'success');
      setShowNovoProduto(false); setNomeNovoProduto('');
      await carregarProdutos();
      setProdutoId(r.produto.id);
    } else showToast?.(r.mensagem || 'Erro ao criar produto', 'error');
  };

  const salvar = async () => {
    if (!produtoId) { showToast?.('Selecione ou crie um produto', 'error'); return; }
    setSalvando(true);
    const r = await apiPat('/assets', {
      method: 'POST',
      body: JSON.stringify({ produto_id: produtoId, asset_tag: assetTag || undefined, projeto_id: projetoId, cliente_id: clienteFiltro || undefined }),
    });
    setSalvando(false);
    if (r.status === 'ok') { showToast?.('Ativo criado!', 'success'); onSalvo(); }
    else showToast?.(r.mensagem || 'Erro ao criar ativo', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Criar ativo" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Criar</Button>}>
      <div className="crm-form-field">
        <label className="crm-form-label">Produto</label>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <select className="crm-select" value={produtoId} onChange={(e) => setProdutoId(e.target.value)} style={{ flex: 1 }}>
            <option value="">Selecione...</option>
            {produtos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={() => setShowNovoProduto(true)}>+ Criar</Button>
        </div>
      </div>
      {showNovoProduto && (
        <div className="crm-panel" style={{ padding: 'var(--crm-space-3)', display: 'flex', gap: 'var(--crm-space-2)' }}>
          <input className="crm-input" placeholder="Nome do produto (rastreável)" value={nomeNovoProduto} onChange={(e) => setNomeNovoProduto(e.target.value)} />
          <Button size="sm" variant="primary" onClick={criarProdutoInline}>Salvar</Button>
        </div>
      )}
      <div className="crm-form-field">
        <label className="crm-form-label">Código do ativo (opcional — gerado automaticamente se vazio)</label>
        <input className="crm-input" value={assetTag} onChange={(e) => setAssetTag(e.target.value)} />
      </div>
    </Sheet>
  );
}

function GerarDeCompraSheet({ projetoId, clienteFiltro, onFechar, onSalvo, showToast }) {
  const [compras, setCompras] = useState(null);
  const [itemSelecionado, setItemSelecionado] = useState('');
  const [statusItem, setStatusItem] = useState(null);
  const [quantidade, setQuantidade] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { apiEst('/compras').then((r) => { if (r.status === 'ok') setCompras(r.lista); }); }, []);

  const itensDisponiveis = (compras || []).flatMap((c) => (c.itens || []).map((it) => ({ ...it, compra_id: c.id, compra_data: c.data })));

  useEffect(() => {
    if (!itemSelecionado) { setStatusItem(null); return; }
    apiPat(`/purchase-items/${itemSelecionado}/status`).then((r) => { if (r.status === 'ok') setStatusItem(r); else setStatusItem(null); });
  }, [itemSelecionado]);

  const salvar = async () => {
    if (!itemSelecionado) { showToast?.('Selecione o item de compra', 'error'); return; }
    setSalvando(true);
    const body = { purchase_item_id: itemSelecionado };
    if (quantidade) body.quantidade = parseInt(quantidade, 10);
    const gerado = await apiPat('/assets/gerar-de-compra', { method: 'POST', body: JSON.stringify(body) });
    if (gerado.status !== 'ok') { setSalvando(false); showToast?.(gerado.mensagem || 'Erro ao gerar ativos', 'error'); return; }
    // vincula automaticamente ao projeto/cliente atual — sem isso o usuário
    // teria que ir em cada ativo gerado individualmente (§30, poucos cliques).
    await Promise.all(gerado.lista.map((a) => apiPat(`/assets/${a.id}/entregar`, {
      method: 'POST', body: JSON.stringify({ projeto_id: projetoId, cliente_id: clienteFiltro || undefined }),
    })));
    setSalvando(false);
    showToast?.(`${gerado.lista.length} ativo(s) gerado(s) e vinculado(s) ao projeto!`, 'success');
    onSalvo();
  };

  return (
    <Sheet open onClose={onFechar} title="Gerar ativos de compra" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Gerar</Button>}>
      {compras === null ? (
        <div className="crm-form-hint">Carregando...</div>
      ) : itensDisponiveis.length === 0 ? (
        <div className="crm-form-hint">Nenhuma compra registrada ainda. Registre uma compra em Estoque primeiro.</div>
      ) : (
        <>
          <div className="crm-form-field">
            <label className="crm-form-label">Item de compra</label>
            <select className="crm-select" value={itemSelecionado} onChange={(e) => setItemSelecionado(e.target.value)}>
              <option value="">Selecione...</option>
              {itensDisponiveis.map((it) => <option key={it.id} value={it.id}>{fmtDate(it.compra_data)} — {it.quantidade}x</option>)}
            </select>
          </div>
          {statusItem && (
            <div className="crm-form-hint">
              {statusItem.tipo_controle !== 'RASTREAVEL' ? 'Este produto não é rastreável — não é possível gerar ativos individuais.'
                : <>Comprado: {statusItem.quantidade_comprada} · Já individualizado: {statusItem.ja_individualizados} · Restante: <strong>{statusItem.restante}</strong></>}
            </div>
          )}
          <div className="crm-form-field">
            <label className="crm-form-label">Quantidade (vazio = todo o restante)</label>
            <input className="crm-input" type="number" min="1" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} />
          </div>
        </>
      )}
    </Sheet>
  );
}

// ─── Aba Financeiro ──────────────────────────────────────────────────────
function AbaFinanceiro({ projetoId, projeto, clienteFiltro, showToast, onMudou, onAbrirAssinatura, onNavegar }) {
  const [linhas, setLinhas] = useState(null);
  const [showNovo, setShowNovo] = useState(null); // 'receita' | 'despesa' | 'assinatura' | null
  const [form, setForm] = useState({ descricao: '', valor: '', vencimento: '', dia_vencimento: '10' });
  const [salvando, setSalvando] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [competencia, setCompetencia] = useState('');
  const [mostrarArquivados, setMostrarArquivados] = useState(false);

  const carregar = useCallback(async () => {
    const params = new URLSearchParams({ projeto_id: projetoId });
    if (clienteFiltro) params.set('cliente_id', clienteFiltro);
    if (competencia) params.set('competencia', competencia);
    if (mostrarArquivados) params.set('incluir_arquivados', '1');
    const [r, p] = await Promise.all([apiFin(`/receivables?${params}`), apiFin(`/payables?${params}`)]);
    const rec = (r.lista || []).map((x) => ({ ...x, _tipo: x.tipo === 'ASSINATURA' ? 'Assinatura' : 'Receita', _origem: 'receivable' }));
    const pag = (p.lista || []).map((x) => ({ ...x, _tipo: 'Despesa', _origem: 'payable' }));
    setLinhas([...rec, ...pag].sort((a, b) => (a.vencimento < b.vencimento ? 1 : -1)));
  }, [projetoId, clienteFiltro, competencia, mostrarArquivados]);

  useEffect(() => { carregar(); }, [carregar]);

  const nomeCliente = (id) => (projeto.clientes.find((c) => c.id === id) || {}).nome || 'Cliente';

  const salvarNovo = async () => {
    if (!form.descricao.trim() || !form.valor) { showToast?.('Preencha descrição e valor', 'error'); return; }
    const cliente_id = clienteFiltro || undefined;
    setSalvando(true);
    let result;
    if (showNovo === 'assinatura') {
      result = await apiFin('/recurrence-rules', {
        method: 'POST',
        body: JSON.stringify({ tipo: 'RECEITA', descricao: form.descricao, valor: form.valor, dia_vencimento: parseInt(form.dia_vencimento, 10), data_inicio: new Date().toISOString().slice(0, 10), projeto_id: projetoId, cliente_id }),
      });
    } else if (showNovo === 'receita') {
      if (!form.vencimento) { showToast?.('Informe o vencimento', 'error'); setSalvando(false); return; }
      result = await apiFin('/receivables', {
        method: 'POST',
        body: JSON.stringify({ tipo: 'AVULSO', descricao: form.descricao, competencia: form.vencimento.slice(0, 7), valor: form.valor, vencimento: form.vencimento, projeto_id: projetoId, cliente_id }),
      });
    } else {
      if (!form.vencimento) { showToast?.('Informe o vencimento', 'error'); setSalvando(false); return; }
      result = await apiFin('/payables', {
        method: 'POST',
        body: JSON.stringify({ tipo: 'DESPESA', descricao: form.descricao, competencia: form.vencimento.slice(0, 7), valor_base: form.valor, vencimento: form.vencimento, projeto_id: projetoId, cliente_id }),
      });
    }
    setSalvando(false);
    if (result.status === 'ok') {
      showToast?.('Criado com sucesso!', 'success');
      setShowNovo(null); setForm({ descricao: '', valor: '', vencimento: '', dia_vencimento: '10' });
      carregar(); onMudou?.();
    } else showToast?.(result.mensagem || 'Erro ao criar', 'error');
  };

  if (linhas === null) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando financeiro...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--crm-space-2)', marginBottom: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
            <span className="crm-form-hint">Competência:</span>
            <input className="crm-input" type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} style={{ maxWidth: '150px' }} />
            {competencia && <Button size="sm" variant="ghost" onClick={() => setCompetencia('')}>Limpar</Button>}
            {!competencia && <span className="crm-form-hint">(mostrando todas)</span>}
          </label>
          <SegmentedControl
            size="sm"
            value={mostrarArquivados}
            onChange={setMostrarArquivados}
            options={[{ value: false, label: 'Ativos' }, { value: true, label: 'Todos' }]}
          />
        </div>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <Button size="sm" variant="outline" onClick={() => setShowNovo('receita')}>+ Receita</Button>
          <Button size="sm" variant="outline" onClick={() => setShowNovo('assinatura')}>+ Assinatura</Button>
          <Button size="sm" variant="outline" onClick={() => setShowNovo('despesa')}>+ Despesa</Button>
        </div>
      </div>

      <Panel flush>
        {linhas.length === 0 ? (
          <EmptyState title={`Nenhum lançamento financeiro ${clienteFiltro ? `para ${nomeCliente(clienteFiltro)}` : 'neste projeto'} ainda`} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Descrição</th><th>Tipo</th><th>Cliente</th><th>Competência</th><th>Vencimento</th><th>Status</th><th>Valor</th></tr></thead>
              <tbody>
                {linhas.map((l) => (
                  <tr
                    key={`${l._origem}-${l.id}`}
                    className="clickable"
                    onClick={() => (l.recurrence_rule_id ? onAbrirAssinatura?.(l.recurrence_rule_id) : setItemSelecionado({ ...l, _origem: l._origem }))}
                  >
                    <td className="crm-table-primary">{l.descricao}{l.arquivado_em && <span style={{ marginLeft: 'var(--crm-space-2)' }}><Badge tone="neutral">Arquivada</Badge></span>}</td>
                    <td>{l._tipo}</td>
                    <td>{l.cliente_id ? nomeCliente(l.cliente_id) : '—'}</td>
                    <td>{l.competencia}</td>
                    <td>{fmtDate(l.vencimento)}</td>
                    <td><StatusBadge status={l.situacao} /></td>
                    <td>{fmtBRL(l.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Sheet
        open={!!showNovo} onClose={() => setShowNovo(null)}
        title={`Nova ${showNovo === 'receita' ? 'Receita' : showNovo === 'assinatura' ? 'Assinatura' : 'Despesa'}`}
        footer={<Button variant="primary" loading={salvando} onClick={salvarNovo}>Salvar</Button>}
      >
        <div className="crm-form-field">
          <label className="crm-form-label">Projeto</label>
          <div className="crm-form-hint">{projeto.nome}{clienteFiltro ? ` — ${nomeCliente(clienteFiltro)}` : ' (todos os clientes)'}</div>
        </div>
        <div className="crm-form-field">
          <label className="crm-form-label">Descrição</label>
          <input className="crm-input" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
        </div>
        <div className="crm-form-field">
          <label className="crm-form-label">Valor</label>
          <input className="crm-input" type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} />
        </div>
        {showNovo === 'assinatura' ? (
          <div className="crm-form-field">
            <label className="crm-form-label">Dia de vencimento</label>
            <input className="crm-input" type="number" min="1" max="31" value={form.dia_vencimento} onChange={(e) => setForm({ ...form, dia_vencimento: e.target.value })} />
          </div>
        ) : (
          <div className="crm-form-field">
            <label className="crm-form-label">Vencimento</label>
            <input className="crm-input" type="date" value={form.vencimento} onChange={(e) => setForm({ ...form, vencimento: e.target.value })} />
          </div>
        )}
      </Sheet>

      {itemSelecionado && (
        <LancamentoSheet
          item={itemSelecionado}
          onClose={() => setItemSelecionado(null)}
          onChanged={() => { carregar(); onMudou?.(); }}
          showToast={showToast}
          onNavegar={onNavegar}
          onAbrirAssinatura={onAbrirAssinatura}
        />
      )}
    </div>
  );
}

// ─── Cockpit do projeto ──────────────────────────────────────────────────
function DetalheProjeto({ projetoId, onVoltar, showToast, modulosHabilitados, onNavegar }) {
  const [aba, setAba] = useState('geral');
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [projeto, setProjeto] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const [showAddCliente, setShowAddCliente] = useState(false);
  const [clientesDisponiveis, setClientesDisponiveis] = useState([]);
  const [assinaturaAberta, setAssinaturaAberta] = useState(null);

  const carregarProjeto = useCallback(async () => {
    const result = await apiProj(`/projetos/${projetoId}`);
    if (result.status === 'ok') setProjeto(result.projeto);
  }, [projetoId]);

  const carregarCockpit = useCallback(async () => {
    const qs = clienteFiltro ? `?cliente_id=${clienteFiltro}` : '';
    const result = await apiProj(`/projetos/${projetoId}/cockpit${qs}`);
    if (result.status === 'ok') setCockpit(result);
  }, [projetoId, clienteFiltro]);

  useEffect(() => { carregarProjeto(); }, [carregarProjeto]);
  useEffect(() => { carregarCockpit(); }, [carregarCockpit]);

  const abrirAddCliente = async () => {
    const result = await apiClientesFn('');
    if (result.status === 'ok') {
      const associados = new Set((projeto?.clientes || []).map((c) => c.id));
      setClientesDisponiveis(result.lista.filter((c) => !associados.has(c._id)));
    }
    setShowAddCliente(true);
  };

  const associarCliente = async (clienteId) => {
    const result = await apiProj(`/projetos/${projetoId}/clientes/${clienteId}`, { method: 'POST' });
    if (result.status === 'ok') { setProjeto(result.projeto); setShowAddCliente(false); showToast?.('Cliente associado!', 'success'); }
    else showToast?.(result.mensagem || 'Erro ao associar cliente', 'error');
  };

  const desassociarCliente = async (clienteId) => {
    const result = await apiProj(`/projetos/${projetoId}/clientes/${clienteId}`, { method: 'DELETE' });
    if (result.status === 'ok') {
      setProjeto(result.projeto);
      if (clienteFiltro === clienteId) setClienteFiltro('');
      showToast?.('Cliente desassociado', 'success');
    } else showToast?.(result.mensagem || 'Erro ao desassociar cliente', 'error');
  };

  if (!projeto) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;

  const tabs = [
    { id: 'geral', label: 'Visão Geral' },
    { id: 'operacao', label: 'Operação' },
    { id: 'financeiro', label: 'Financeiro' },
    { id: 'documentos', label: 'Documentos' },
    modulosHabilitados?.patrimonio && { id: 'equipamentos', label: 'Equipamentos' },
    { id: 'historico', label: 'Histórico' },
  ].filter(Boolean);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--crm-space-3)', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)' }}>
          <button className="crm-icon-btn" onClick={onVoltar} aria-label="Voltar"><FontAwesomeIcon icon={faArrowLeft} /></button>
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--crm-text-xl)', color: 'var(--crm-text-primary)' }}>{projeto.nome}</h2>
            {projeto.descricao && <div className="crm-form-hint">{projeto.descricao}</div>}
          </div>
          <StatusBadge status={projeto.status} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)' }}>
          <label className="crm-form-hint">Cliente:</label>
          <select className="crm-select" value={clienteFiltro} onChange={(e) => setClienteFiltro(e.target.value)}>
            <option value="">Todos</option>
            {projeto.clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', flexWrap: 'wrap', marginBottom: 'var(--crm-space-4)' }}>
        <FontAwesomeIcon icon={faUserGroup} style={{ color: 'var(--crm-text-tertiary)' }} />
        {projeto.clientes.length === 0 && <span className="crm-form-hint">Nenhum cliente associado (projeto interno)</span>}
        {projeto.clientes.map((c) => (
          <Badge key={c.id} tone="accent">{c.nome} <span style={{ cursor: 'pointer', marginLeft: 4 }} onClick={() => desassociarCliente(c.id)}>×</span></Badge>
        ))}
        <Button size="sm" variant="outline" icon={faPlus} onClick={abrirAddCliente}>Associar cliente</Button>
      </div>

      <Tabs tabs={tabs} atual={aba} onChange={setAba} />

      <div style={{ marginTop: 'var(--crm-space-4)' }}>
        {aba === 'geral' && <AbaVisaoGeral cockpit={cockpit} />}
        {aba === 'historico' && <AbaHistorico projetoId={projetoId} clienteFiltro={clienteFiltro} />}
        {aba === 'operacao' && <AbaOperacao projetoId={projetoId} projeto={projeto} clienteFiltro={clienteFiltro} showToast={showToast} onMudou={carregarCockpit} />}
        {aba === 'financeiro' && <AbaFinanceiro projetoId={projetoId} projeto={projeto} clienteFiltro={clienteFiltro} showToast={showToast} onMudou={carregarCockpit} onAbrirAssinatura={setAssinaturaAberta} onNavegar={onNavegar} />}
        {aba === 'documentos' && <AbaDocumentosProjeto projetoId={projetoId} clienteFiltro={clienteFiltro} showToast={showToast} />}
        {aba === 'equipamentos' && modulosHabilitados?.patrimonio && <AbaEquipamentosProjeto projetoId={projetoId} clienteFiltro={clienteFiltro} showToast={showToast} onNavegar={onNavegar} />}
      </div>

      <Sheet open={showAddCliente} onClose={() => setShowAddCliente(false)} title="Associar Cliente">
        {clientesDisponiveis.length === 0 ? (
          <div className="crm-form-hint">Todos os clientes já estão associados (ou não há clientes cadastrados).</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-1)' }}>
            {clientesDisponiveis.map((c) => (
              <button key={c._id} className="crm-nav-item" style={{ justifyContent: 'flex-start' }} onClick={() => associarCliente(c._id)}>{c.nome}</button>
            ))}
          </div>
        )}
      </Sheet>

      <AssinaturaSheet
        assinaturaId={assinaturaAberta}
        open={!!assinaturaAberta}
        onClose={() => setAssinaturaAberta(null)}
        onChanged={carregarCockpit}
        showToast={showToast}
        onNavegar={onNavegar}
      />
    </div>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────
function Projetos({ showToast, modulosHabilitados, projetoIdInicial, clienteIdParaNovoProjeto, onNavegar }) {
  const [projetoSelecionado, setProjetoSelecionado] = useState(projetoIdInicial || null);
  if (projetoSelecionado) {
    return <DetalheProjeto projetoId={projetoSelecionado} onVoltar={() => setProjetoSelecionado(null)} showToast={showToast} modulosHabilitados={modulosHabilitados} onNavegar={onNavegar} />;
  }
  return (
    <ListaProjetos
      onAbrirProjeto={setProjetoSelecionado}
      showToast={showToast}
      clienteIdParaNovoProjeto={clienteIdParaNovoProjeto}
      onProjetoCriado={setProjetoSelecionado}
    />
  );
}

export default Projetos;
